/**
 * An unlocked session never stands open behind a sign-in screen (spec 15.3; plan 5 final
 * review, H1). Every way out of it to Masuk (a session GoTrue ended at a step, a recovery
 * mid-session, Akun's "Masuk lagi", a step whose sign-in was lost, the address typed) lands on
 * Masuk, and Masuk locks the session it finds open: "Buka dengan PIN" then asks for the PIN.
 * The lock is taken where the exits land rather than before each one navigates: a session locked
 * on the screen being left is redirected from there to the PIN first (the guard does it for
 * Akun, a step screen for itself), which would win over the way to Masuk. The PIN screen never
 * opens a session it did not unlock itself. DOM-free so the tests import it; MasukRoute, in the
 * entry chunk, keeps its import tiny.
 */
import type { SesiStatus } from '../../app/store/sesi'
import type { PinHasil } from '../../auth/session'

/**
 * What the lock hands the PIN screen (../../auth/guard): where it caught the person, and whose
 * session it was. A step screen locked mid-session (./arahLangkah) and Lupa PIN's way back
 * hand it the same, without `keadaanDari`.
 */
export interface KeadaanKunci {
  dari: string
  /**
   * What the locked entry's state held that comes back with it (a list's search, a selection
   * sheet's ticked rows).
   */
  keadaanDari?: Record<string, unknown> | null
  /** The user id of the session the lock caught (sesi.userId). */
  untuk: string
}

/**
 * Where unlocking returns, from the lock's state (`KeadaanKunci`): the page the lock caught and what its entry held (a list's search), only for the
 * person it caught (`untuk`, their user id). On a shared device the tab may hold another
 * person's lock (they were locked here, then someone else signed in, and the tab took the
 * database back and reloaded): unlocking with one's own PIN then starts at the home page,
 * never on their page or with their search.
 */
export function tujuanBuka(
  state: unknown,
  pengguna: string,
): { dari: string; keadaanDari: unknown } {
  const k = (typeof state === 'object' && state !== null ? state : {}) as Partial<KeadaanKunci>
  if (k.untuk !== pengguna || typeof k.dari !== 'string') return { dari: '/', keadaanDari: null }
  return { dari: k.dari, keadaanDari: k.keadaanDari ?? null }
}

/**
 * Masuk locks the session it finds open; never the one its own sign-in just opened (the same
 * person signed in again goes straight on, `masukDariSini` from the press on).
 */
export function kunciDiMasuk(status: SesiStatus, masukDariSini: boolean): boolean {
  return status === 'aktif' && !masukDariSini
}

/**
 * What the PIN screen does with the session it finds: `tunggu` (still loading), `masuk` (none),
 * `pin` (locked: the PIN is asked), `lanjut` (its own unlock went through), `kunci` (found
 * open without its own unlock: locked, so the PIN is asked, never skipped).
 */
export function arahPin(
  status: SesiStatus,
  bukaSendiri: boolean,
): 'tunggu' | 'masuk' | 'pin' | 'lanjut' | 'kunci' {
  switch (status) {
    case 'memuat':
      return 'tunggu'
    case 'tanpa_sesi':
      return 'masuk'
    case 'terkunci':
      return 'pin'
    case 'aktif':
      return bukaSendiri ? 'lanjut' : 'kunci'
  }
}

/**
 * The PIN screen's unlock: busy from the press until `buka` answers or throws (a worker that
 * refused, a storage error), never longer. Only an unlock that went through stays the screen's
 * own (`membuka`), so the session it opened goes on instead of being locked again; one refused,
 * or one that threw, leaves the screen as it was. A throw still reaches the caller.
 */
export async function bukaDenganPin(
  buka: () => Promise<PinHasil>,
  layar: { sibuk(nilai: boolean): void; membuka(nilai: boolean): void },
): Promise<PinHasil> {
  layar.sibuk(true)
  layar.membuka(true)
  let terbuka = false
  try {
    const hasil = await buka()
    terbuka = hasil.ok
    return hasil
  } finally {
    layar.sibuk(false)
    if (!terbuka) layar.membuka(false)
  }
}
